// A type utility to convert a tuple of types into a single intersection type.
type TupleToIntersection<T extends Record<string, string>[]> = {
  [I in keyof T]: (x: T[I]) => void;
}[number] extends (x: infer I) => void
  ? I
  : never;

/** A type-safe function to merge records with a generic return type. */
export function createInvertedMessages<T extends Record<string, string>[]>(
  records: [...T]
): TupleToIntersection<T> {
  // Temporary objects used only for validation of messages.
  const messages: Record<string, string> = {};
  const inverted: Record<string, string> = {};
  const errors: string[] = [];

  // A helper function to validate and insert key-value pairs.
  const insertKeyValue = (key: string, value: string) => {
    if (messages[key]) {
      errors.push(
        `Duplicate key "${key}": Used by "${value}" and "${messages[key]}".`
      );
    }

    if (inverted[value]) {
      errors.push(
        `Duplicate message "${value}": Used by "${key}" and "${inverted[value]}".`
      );
    }

    inverted[value] = key;
    messages[key] = value;
  };

  // Combine into a new object in a type safe manner.
  const ret = records.reduce((merged, currentRecord) => {
    // Validate the keys and values in the current record to avoid duplicates
    Object.keys(currentRecord).forEach((value) => {
      const key = currentRecord[value as keyof typeof currentRecord];

      insertKeyValue(key, value);
    });

    return { ...merged, ...currentRecord };
  }, {}) as TupleToIntersection<T>; // Type assertion is needed here

  if (errors.length) {
    throw new Error(`\n${errors.join('\n')}`);
  }

  return ret;
}
